<script setup lang="ts">
import UIBody from '@/components/ui/UIBody.vue'
import UIToolTip from '@/components/ui/UIToolTip.vue'
import useFullRemedies from '@/composables/data/useFullRemedies'
import { useAnalytics } from '@/composables/useAnalytics'
import { useInlineLanguage } from '@/composables/useLanguage'
import { useGetDataLayers } from '@/services/adapters/useGetDataLayers'
import { useGetEffects } from '@/services/adapters/useGetEffects'
import { useReferencesStore } from '@/store/references'
import {
  IPillLinkItem,
  TTooltipAlignment,
  TTooltipDirection
} from '@lifk/types'
import { computed } from 'vue'

const props = defineProps<{
  item: IPillLinkItem
  tooltipDirection: TTooltipDirection
  tooltipAlignment: TTooltipAlignment
  /** Should the tooltip be disabled (primarily for link items that appear within a tooltip already)? */
  tooltipDisabled?: boolean
}>()

const { t } = useInlineLanguage()
const referencesStore = useReferencesStore()
const { data: effects } = useGetEffects()
const { fullRemedies: remedies } = useFullRemedies(false) // filter out deprecated remedies
const { data: dataLayers } = useGetDataLayers()
const { trackButtonClick } = useAnalytics()

const clicksEnabled = computed(() => {
  switch (props.item.linkType) {
    case 'remedy':
      return !!remedies.value?.find(({ key }) => key === props.item.key)
    case 'effect':
      return !!effects.value?.find(({ key }) => key === props.item.key)
    case 'dataLayer':
      return !!dataLayers.value?.find(({ key }) => key === props.item.key)
    default:
      return false
  }
})

const referencesLinkClicked = () => {
  if (clicksEnabled.value) {
    referencesStore.openReference(
      props.item.linkType,
      props.item.key,
      'references'
    )
    trackButtonClick({
      action: 'viewed_guidebook',
      location: 'ui_pill_link_item',
      value: {
        key: props.item.key,
        name: props.item.name['en-us']
      }
    })
  }
}
</script>

<template>
  <div class="w-fit">
    <UIToolTip
      :direction="tooltipDirection"
      :alignment="tooltipAlignment"
      variant="light"
      :disabled="tooltipDisabled"
      :reference="item"
      class="group rounded !text-left"
    >
      <UIBody
        weight="medium"
        :style="[
          { '--pill-color': item.pillColor },
          { '--text-color': item.textColor }
        ]"
        class="underline-pill w-fit relative z-2 cursor-pointer text-left inline px-[3px] py-0.5 mb-0.5"
        tabindex="0"
        role="link"
        @click.stop.prevent="referencesLinkClicked"
      >
        {{ t(item.name) }}
      </UIBody>
    </UIToolTip>
  </div>
</template>

<style scoped>
.underline-pill {
  @apply rounded-none box-decoration-clone bg-no-repeat;
  background-position: 0 100%;
  background-size: 100% 2px;
  background-image: linear-gradient(var(--pill-color), var(--pill-color));
  @apply transition-[background-size,color] duration-150;
  @apply group-hover:rounded-xs group-hover:text-[color:var(--text-color)] group-hover:[background-size:100%_100%];
}
</style>
